import * as React from 'react';
import { FC, useCallback, useEffect, useMemo, useState } from 'react';

import { usePathname, useRouter } from 'expo-router';
import { useIntl } from 'react-intl';

import { IHistoricalOrder } from '@rbi-ctg/menu';
import { Helmet } from 'components/helmet';
import { InnerLayout, LayoutContainer } from 'components/layout/styled';
import { BackArrowHistory } from 'components/navigation/back-arrow';
import { IUserOrderFragment, useGetUserOrdersQuery } from 'generated/graphql-gateway';
import { RbiOrderStatus } from 'generated/graphql-gateway';
import { useRoute } from 'hooks/navigation/use-route';
import { useRefundEligibility } from 'hooks/refund-eligibility';
import { useOrderDetails } from 'hooks/use-order-details';
import { useSupportData } from 'hooks/use-support-data';
import { appendObjectToQueryString } from 'navigation/utils';
import { CustomEventNames, EventTypes, logEvent } from 'state/amplitude';
import { useAuthContext } from 'state/auth';
import { routes } from 'utils/routing';

import { ErrorView } from '../error-view';
import { LoadingView } from '../loading-view';
import { ISupportPageV2Props } from '../types';

import { SupportV2OrdersListView } from './support-v2-orders-list-view';
import { getDropdownOptions, groupOrdersByYearAndMonth } from './utils';

/**
 * Displays list of a users recent orders for them to select from
 */
const SupportV2OrdersListPage: FC<React.PropsWithChildren<unknown>> = () => {
  const { formatMessage, locale } = useIntl();
  const pathname = usePathname();
  const { navigate, replace } = useRouter();
  const { params } = useRoute<ISupportPageV2Props>();
  const { orderId: preselectedOrderId, subcategoryId, categoryId } = params;

  const {
    order: preselectedOrder,
    loading: isPreselectedOrderLoading,
    error: preselectedOrderError,
  } = useOrderDetails(preselectedOrderId || null);

  const { isAuthenticated, setOriginLocation, openSignInModal } = useAuthContext();

  /** Tech Debt
   * There is a similar query in the useUserOrders hook which should be updated to use useGetUserOrdersQuery as well.
   * Then this `useGetUserOrdersQuery` could be replaced with the `useUserOrders` hook, removing duplication
   */
  const { data: userOrders, loading: isLoadingOrders } = useGetUserOrdersQuery({
    variables: {
      orderStatuses: [
        RbiOrderStatus.INSERT_SUCCESSFUL,
        RbiOrderStatus.UPDATE_SUCCESSFUL,
        RbiOrderStatus.REFUND_SUCCESSFUL,
        RbiOrderStatus.REFUND_ERROR,
        RbiOrderStatus.REFUND_REQUESTED,
      ],
      shouldFilterByRegion: true,
    },
    skip: !isAuthenticated || Boolean(preselectedOrderId),
    context: { shouldNotBatch: true },
  });
  const pageTitleText = formatMessage({ id: 'recentOrders' });
  const orders = (userOrders?.userOrders.orders || []) as IUserOrderFragment[];
  const [selectedYearAndMonth, setSelectedYearAndMonth] = useState('');
  const [selectedOrder, setSelectedOrder] = useState<IUserOrderFragment | IHistoricalOrder | null>(
    null
  );

  const {
    isSupportDataLoading,
    deliveryNeverArriveSubcategory,
    deliveryMissingItemsSubcategory,
  } = useSupportData();

  const { isDeterminingRefundEligibility, refundIneligibilityReason } = useRefundEligibility({
    orderId: selectedOrder?.rbiOrderId || preselectedOrderId,
  });

  const logNavigationDecision = useCallback(
    (message: any) =>
      logEvent(CustomEventNames.SUPPORT_FORM_NAVIGATION_DECISION, EventTypes.Navigation, {
        message: `Orders List Page: ${message}`,
        orderId: selectedOrder?.rbiOrderId,
      }),
    [selectedOrder]
  );

  useEffect(() => {
    if (preselectedOrder) {
      setSelectedOrder(preselectedOrder);
    }
  }, [preselectedOrder, setSelectedOrder]);

  /** When an order is selected and eligibility has been determined. */
  useEffect(() => {
    if (isAuthenticated && !isDeterminingRefundEligibility && !isPreselectedOrderLoading) {
      const orderId = selectedOrder?.rbiOrderId || preselectedOrderId;

      if (selectedOrder) {
        // If orderId is in route, going back would send them forward again.
        const isOrderIdPreselected = Boolean(preselectedOrderId);

        logNavigationDecision(
          'customer chose an order not eligible for a refund, navigating to support request form'
        );

        const routeWithQueryString = appendObjectToQueryString(`${routes.supportForm}`, {
          ...(categoryId && { categoryId }),
          ...(subcategoryId && { subcategoryId }),
          ...(orderId && { orderId }),
          alertMessage: refundIneligibilityReason,
        });
        return isOrderIdPreselected
          ? replace(routeWithQueryString)
          : navigate(routeWithQueryString);
      }
    }
  }, [
    isDeterminingRefundEligibility,
    selectedOrder,
    deliveryMissingItemsSubcategory,
    deliveryNeverArriveSubcategory,
    refundIneligibilityReason,
    subcategoryId,
    params,
    categoryId,
    isAuthenticated,
    preselectedOrderId,
    isPreselectedOrderLoading,
    logNavigationDecision,
    navigate,
    replace,
  ]);

  /**
   * If the user has no orders, take them straight to the support form.
   */
  useEffect(() => {
    if (!isLoadingOrders && userOrders?.userOrders.orders?.length === 0) {
      const routeWithQueryString = appendObjectToQueryString(`${routes.supportForm}`, {
        ...(categoryId && { categoryId }),
        ...(subcategoryId && { subcategoryId }),
      });
      replace(routeWithQueryString);
    }
  }, [categoryId, isLoadingOrders, replace, subcategoryId, userOrders]);

  const ordersGroupedByYearAndMonth = useMemo(() => groupOrdersByYearAndMonth(orders), [orders]);
  const dropdownOptions = useMemo(() => getDropdownOptions(ordersGroupedByYearAndMonth, locale), [
    locale,
    ordersGroupedByYearAndMonth,
  ]);

  const onClickSignin = () => {
    setOriginLocation(appendObjectToQueryString(pathname, params));
    openSignInModal();
  };

  /**
   * set this value after the orders have loaded and `dropdownOptions` has values
   */
  useEffect(() => {
    if (dropdownOptions?.length) {
      setSelectedYearAndMonth(dropdownOptions[0].value);
    }
  }, [dropdownOptions]);

  const handleChangeYearAndMonth = useCallback((value: string) => setSelectedYearAndMonth(value), [
    setSelectedYearAndMonth,
  ]);

  if (
    isLoadingOrders ||
    isSupportDataLoading ||
    isDeterminingRefundEligibility ||
    /**
     * The page should stay in a loading state waiting for
     * a redirect. order or order loading in case it errors.
     */
    preselectedOrder ||
    isPreselectedOrderLoading
  ) {
    return <LoadingView />;
  }

  if ((isAuthenticated && !userOrders?.userOrders) || preselectedOrderError) {
    return <ErrorView message={formatMessage({ id: 'looksLikeWereExperiencingIssues' })} />;
  }

  return (
    <LayoutContainer>
      <InnerLayout>
        <Helmet title={pageTitleText} />
        <BackArrowHistory />
        <SupportV2OrdersListView
          dropdownOptions={dropdownOptions}
          items={ordersGroupedByYearAndMonth[selectedYearAndMonth] || []}
          onYearAndMonthChange={handleChangeYearAndMonth}
          selectedYearAndMonth={selectedYearAndMonth}
          isAuthenticated={isAuthenticated}
          onClickSignin={onClickSignin}
          onSelectOrder={setSelectedOrder}
        />
      </InnerLayout>
    </LayoutContainer>
  );
};

export default SupportV2OrdersListPage;
